'use client';

import type {ReactNode} from 'react';
import {Suspense} from 'react';
import {keepPreviousData, useQuery} from '@tanstack/react-query';
import clsx from 'clsx';
import {useSearchParams} from 'next/navigation';

import type {CompaniesResponse} from '@/app/(frontend)/api/companies/schema';
import {Alert} from '@/modules/finkn/components/alert';
import {Badge} from '@/modules/finkn/components/badge';
import {Skeleton} from '@/modules/finkn/components/skeleton';
import {CategoryFilters} from '@/modules/finkn/shared/components/filters/category-filters';
import {searchParamsToFilters} from '@/modules/finkn/shared/components/filters/search-params-filter';
import type {ContentBlockContainerProps} from '@/modules/page/components/content-blocks/block-container';
import {ContentBlockContainer} from '@/modules/page/components/content-blocks/block-container';

interface Props extends ContentBlockContainerProps {
	info?: {
		title?: string;
		description?: ReactNode;
	};
	insideToc?: boolean;
}

const fetchCompanies = async (): Promise<CompaniesResponse> => {
	const query = `/api/companies`;
	const response = await fetch(query);

	if (!response.ok) {
		const errorData = await response.json();
		throw new Error(errorData.message || 'Failed to fetch companies');
	}

	return await response.json();
};

export const CompaniesListAll = ({options, info, insideToc}: Props) => {
	const searchParams = useSearchParams();

	const filters = searchParamsToFilters(searchParams) || {fieldOfArea: []};

	const {status, data, isFetching} = useQuery({
		queryKey: ['companies'],
		queryFn: () => fetchCompanies(),
		placeholderData: keepPreviousData,
		staleTime: 10 * 60 * 1000, // 10 minutes
	});

	const filteredCompanies = data?.filter((company) => {
		if (filters.fieldOfArea.length === 0) {
			return true;
		}

		if (
			filters.fieldOfArea.includes('generelt') &&
			!company.CompanyTypeName
		) {
			return true;
		}

		if (company.CompanyTypeName) {
			return filters.fieldOfArea.includes(
				company.CompanyTypeName.toLowerCase(),
			);
		}

		return false;
	});

	return (
		<ContentBlockContainer options={options} insideToc={insideToc}>
			<div className="@container">
				<section className={clsx('grid', '@2xl:grid-cols-3', 'gap-10')}>
					<aside className={clsx('@2xl:col-span-1')}>
						<Suspense>
							<CategoryFilters
								hideGenereltFilter
								fieldOfAreaFilters={[
									{
										slug: 'forsikring',
										title: 'Forsikring',
										_id: 'forsikring',
									},
									{
										slug: 'bank',
										title: 'Bank',
										_id: 'bank',
									},
									{
										slug: 'inkasso',
										title: 'Inkasso',
										_id: 'inkasso',
									},
								]}
							/>
						</Suspense>
					</aside>
					<div className={clsx('@2xl:col-span-2', 'max-w-[740px]')}>
						{status === 'pending' || isFetching ? (
							<div>
								<CompanyListItemSkeleton />
								<CompanyListItemSkeleton />
								<CompanyListItemSkeleton />
							</div>
						) : status === 'error' ? (
							<div className={clsx('flex', 'flex-col', 'gap-6')}>
								<Alert
									severity="error"
									title="Beklager, noe gikk galt"
								>
									Vi har problemer med å hente foretak. Du kan
									vente noen minutter og laste siden på nytt.
								</Alert>

								{info?.title && (
									<Alert title={info.title} severity="info">
										{info.description}
									</Alert>
								)}
							</div>
						) : (
							<div className={clsx('flex', 'flex-col', 'gap-6')}>
								<ul>
									{filteredCompanies?.map((company, idx) => (
										<CompanyListItem
											key={`${idx}-${company.OrgNo}`}
											company={company}
										/>
									))}
								</ul>

								{filteredCompanies?.length === 0 && (
									<Alert
										title="Ingen selskaper funnet"
										severity="warning"
									>
										Vi fant ingen selskaper som passer til
										dine filtre.
									</Alert>
								)}

								{info?.title && (
									<Alert title={info.title} severity="info">
										{info.description}
									</Alert>
								)}
							</div>
						)}
					</div>
				</section>
			</div>
		</ContentBlockContainer>
	);
};

const CompanyListItem = ({company}: {company: CompaniesResponse[number]}) => {
	return (
		<li
			className={clsx(
				'py-6',
				'border-b',
				'first:border-t',
				'border-grey-border',
				'flex',
				'items-center',
				'flex-wrap',
				'gap-x-6',
				'gap-y-2',
				'hover:bg-grey-95',
				'transition-colors',
			)}
		>
			{company.CompanyName}
			<Badge variant="outline">{company.CompanyTypeName}</Badge>
		</li>
	);
};

const CompanyListItemSkeleton = () => {
	return (
		<div
			className={clsx(
				'py-6',
				'border-b',
				'first:border-t',
				'border-grey-border',
				'flex',
				'items-center',
				'flex-wrap',
				'gap-x-6',
				'gap-y-2',
			)}
		>
			<Skeleton className={clsx('w-2/3', 'h-5')} />
		</div>
	);
};
